import { MessageSquareWarning } from "lucide-react";
import { useState } from "react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getMobileNavClearance } from "../layout/mobileNavClearance";
import { FeedbackDialog } from "./FeedbackDialog";

/**
 * Almikia Epic PL (TASK-061): the "Reportar un problema o sugerencia" entry.
 * Visible to every logged-in role (it is mounted by the layouts, which are
 * never rendered on login / auth pages) and does no work until it is used:
 * the dialog only mounts while open.
 *
 * - `desktop`: a rail-style button in the navy header.
 * - `mobile`: a floating 48px button (>= the 44px touch minimum) pinned
 *   above the floating bottom navigation using `getMobileNavClearance()`, so
 *   it never overlaps the nav or the toast offset.
 */
export const FeedbackButton = ({
  variant,
}: {
  variant: "desktop" | "mobile";
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const label = translate("crm.feedback.button");

  return (
    <>
      {variant === "desktop" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors",
            "text-sidebar-foreground/80 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground",
            "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-brand-yellow",
          )}
        >
          <MessageSquareWarning className="size-5 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </button>
      ) : (
        <Button
          type="button"
          size="icon"
          aria-label={label}
          title={label}
          onClick={() => setOpen(true)}
          className="fixed right-4 z-20 size-12 rounded-full shadow-lg focus-visible:ring-brand-yellow"
          style={{
            bottom: `${getMobileNavClearance()}px`,
            width: 48,
            height: 48,
          }}
        >
          <MessageSquareWarning className="size-5" aria-hidden />
        </Button>
      )}
      {open ? <FeedbackDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
};
